import {
  type ActiveCohortBuilderItemLegacy,
  type CohortBuilderDefinitionLegacy,
  CohortBuilderModalLegacy,
  type InputOptionContextDataLegacy,
} from '@verantos-dev/types-cohorts';
import { type ConceptGroup } from '@verantos-dev/types-concepts';

import { addDerivedEvent } from './actions/addDerivedEvent';
import { addIndexDate } from './actions/addIndexDate';
import { addSimpleEvent } from './actions/addSimpleEvent';
import { addSubjectVariable } from './actions/addSubjectVariable';
import { getRequiredDataFromState } from './utils/getRequiredDataFromState';

/**
 * @description This holds the main logic for the generating the next active cohort builder item state:
 *
 * The goal of this function is to generate the next active cohort builder item state.
 *
 * @param {CohortBuilderDefinition} cohortDefinition
 * @param {CohortBuilderWorkflow} cohortBuilderWorkflow
 * @param {ActiveCohortBuilderItem} nextActiveCohortBuilderItem
 *
 * @return {ActiveCohortBuilderItem} The next active cohort builder item
 */
const getNextActiveCohortBuilderItemState = (
  modal: CohortBuilderModalLegacy | undefined,
  cohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  /**
   * Validate that the cohort definition and workflow exist
   */
  if (!cohortDefinition) {
    throw new Error(
      `The cohort definition must be defined.\ncohortDefinition: ${cohortDefinition}`
    );
  }

  /**
   * Get the required data from the state to perform calculations on the next active cohort builder item
   */
  const {
    /** The value of the current cohort builder item in the definition */

    /** data from cohortDefinition */
    simpleEvents,
    derivedEvents,

    /** data from nextActiveCohortBuilderItem */
    name,
    type,
  } = getRequiredDataFromState(cohortDefinition, nextActiveCohortBuilderItem, modal);

  /**
   * Begin the runtime check to generate options and validate the next active cohort builder item
   */
  if (modal !== undefined) {
    // only enter runtime if there is an active modal defined

    /**
     * Runtime check calculations
     * We only want to execute the runtime once some preliminary values have been entered by the user
     */
    const nameCheck =
      name || // Name must be defined
      [CohortBuilderModalLegacy.ADD_INDEX_DATE, CohortBuilderModalLegacy.EDIT_INDEX_DATE].includes(
        modal
      ); // Name is not required for index date
    const typeCheck = type; // Type must be defined
    if (nameCheck && typeCheck) {
      switch (modal) {
        case CohortBuilderModalLegacy.EDIT_SIMPLE_EVENT:
        case CohortBuilderModalLegacy.ADD_SIMPLE_EVENT: {
          return addSimpleEvent(
            nextActiveCohortBuilderItem,
            inputOptionContextData.conceptGroups as ConceptGroup[]
          );
        }
        case CohortBuilderModalLegacy.EDIT_DERIVED_EVENT:
        case CohortBuilderModalLegacy.ADD_DERIVED_EVENT: {
          return addDerivedEvent(nextActiveCohortBuilderItem, simpleEvents, derivedEvents);
        }
        case CohortBuilderModalLegacy.ADD_SUBJECT_VARIABLE:
        case CohortBuilderModalLegacy.EDIT_SUBJECT_VARIABLE: {
          return addSubjectVariable(
            nextActiveCohortBuilderItem,
            simpleEvents,
            derivedEvents,
            inputOptionContextData.conceptGroups as ConceptGroup[]
          );
        }
        case CohortBuilderModalLegacy.ADD_INDEX_DATE:
        case CohortBuilderModalLegacy.EDIT_INDEX_DATE: {
          return addIndexDate(nextActiveCohortBuilderItem, simpleEvents, derivedEvents);
        }
      }
    }
  }

  return nextActiveCohortBuilderItem;
};

/**
 * Finds the next active cohort builder item state
 *
 * @param {CohortBuilderDefinition} cohortDefinition
 * @param {CohortBuilderWorkflow} cohortBuilderWorkflow
 * @param {ActiveCohortBuilderItem} nextActiveCohortBuilderItem
 *
 * @returns {ActiveCohortBuilderItem} The next active cohort builder item
 */
export const recursiveCheckNextActiveCohortBuilderItemState = (
  modal: CohortBuilderModalLegacy | undefined,
  cohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  const _nextActiveCohortBuilderItem = getNextActiveCohortBuilderItemState(
    modal,
    cohortDefinition,
    nextActiveCohortBuilderItem,
    inputOptionContextData
  );
  if (
    JSON.stringify(nextActiveCohortBuilderItem) !== JSON.stringify(_nextActiveCohortBuilderItem)
  ) {
    return recursiveCheckNextActiveCohortBuilderItemState(
      modal,
      cohortDefinition,
      _nextActiveCohortBuilderItem,
      {
        ...inputOptionContextData,
        activeCohortBuilderItem: _nextActiveCohortBuilderItem,
      }
    );
  }
  return _nextActiveCohortBuilderItem;
};
